import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TCreateProjectViewRequest,
  TCreateWorkspaceViewRequest,
  TProjectView,
  TSavedViewShares,
  TSavedViewSharesRequest,
  TUpdateProjectViewRequest,
  TUpdateWorkspaceViewRequest,
  TWorkspaceView,
} from "@/types/view";

export class ViewService extends ApiService {
  async listProjectViews(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectView[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/views/`, config);
  }

  async createProjectView(
    slug: string,
    projectId: string,
    data: TCreateProjectViewRequest,
  ): Promise<TProjectView> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/views/`, data);
  }

  async getProjectView(
    slug: string,
    projectId: string,
    viewId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectView> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/views/${viewId}/`, config);
  }

  async updateProjectView(
    slug: string,
    projectId: string,
    viewId: string,
    data: TUpdateProjectViewRequest,
  ): Promise<TProjectView> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/views/${viewId}/`, data);
  }

  async deleteProjectView(slug: string, projectId: string, viewId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/projects/${projectId}/views/${viewId}/`);
  }

  async listWorkspaceViews(slug: string, config?: ApiRequestConfig): Promise<TWorkspaceView[]> {
    return this.get(`/api/workspaces/${slug}/views/`, config);
  }

  async createWorkspaceView(
    slug: string,
    data: TCreateWorkspaceViewRequest,
  ): Promise<TWorkspaceView> {
    return this.post(`/api/workspaces/${slug}/views/`, data);
  }

  async getWorkspaceView(
    slug: string,
    viewId: string,
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceView> {
    return this.get(`/api/workspaces/${slug}/views/${viewId}/`, config);
  }

  async updateWorkspaceView(
    slug: string,
    viewId: string,
    data: TUpdateWorkspaceViewRequest,
  ): Promise<TWorkspaceView> {
    return this.patch(`/api/workspaces/${slug}/views/${viewId}/`, data);
  }

  async deleteWorkspaceView(slug: string, viewId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/views/${viewId}/`);
  }

  /** Who a view is shared with (NG-2980). `projectId` for a view inside a project. */
  async getViewShares(
    slug: string,
    viewId: string,
    projectId: string | null,
    config?: ApiRequestConfig,
  ): Promise<TSavedViewShares> {
    return this.get(`${viewBase(slug, projectId)}${viewId}/shares/`, config);
  }

  /** Replace who a view is shared with (NG-2980): the server answers with the new set. */
  async putViewShares(
    slug: string,
    viewId: string,
    projectId: string | null,
    data: TSavedViewSharesRequest,
  ): Promise<TSavedViewShares> {
    return this.put(`${viewBase(slug, projectId)}${viewId}/shares/`, data);
  }
}

function viewBase(slug: string, projectId: string | null): string {
  return projectId
    ? `/api/workspaces/${slug}/projects/${projectId}/views/`
    : `/api/workspaces/${slug}/views/`;
}
